import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { MySitesService } from '../../services/my-sites/my-sites.service';
import type { IMySitesService } from '../../services/my-sites/my-sites.service.interface';
import { useValidatedTenantId } from '../auth/use-validated-tenant-id';
import { mySitesQueryKeys } from './use-my-sites-queries';

/**
 * Whether the signed-in user may open the "Le mie sedi" area. Backed by
 * `GET /my-sites/access`, which is ALWAYS 200 (never behind a guard): the
 * backend's own `hasMultiSiteAccess` (>=2 sedi come Owner/Manager) is the
 * signal, never re-derived from memberships client-side.
 *
 * The query key includes the current tenantId, mirroring `useConsultantAccess`
 * (`hooks/consultant/use-consultant-access.ts`): defensive against showing a
 * stale flag from the previous tenant while the switch settles.
 */
export function useMultiSiteAccess() {
  const mySitesService = useInjection<IMySitesService>(MySitesService);
  const tenantId = useValidatedTenantId();

  const {
    data: access,
    isPending,
    fetchStatus,
  } = useQuery({
    queryKey: mySitesQueryKeys.access(tenantId),
    queryFn: () => mySitesService.getAccess(),
    staleTime: 5 * 60 * 1000,
  });

  return {
    hasMultiSiteAccess: access?.hasMultiSiteAccess ?? false,
    siteCount: access?.siteCount ?? 0,
    isPending,
    fetchStatus,
  };
}
